/** Durable mutations are sent once; background refreshes only read server state. */
export const COLLECTIONS = ['rooms', 'meetings', 'members', 'invitations', 'attendance', 'audit'] as const;
export type Collection = typeof COLLECTIONS[number];
export type Rows = Record<string, any>[];
export type SyncState = Record<Collection, Rows>;
export interface Mutation { opId: string; kind: 'patch' | 'delete' | 'clear' | 'delete_session' | 'seed'; collection?: Collection; items?: Rows; ids?: string[]; sessionId?: string; roomSlug?: string; dateStr?: string; seed?: SyncState }
export interface SyncResult { success: boolean; message: string; pending: number; authorizationLost?: boolean }
export class SyncAuthorizationError extends Error {}
export interface TenantSyncOptions { organizationId: string; token: string }
const copy = <T>(v: T): T => JSON.parse(JSON.stringify(v));
export function attendanceSessionId(row: Record<string, any>): string {
  return row.academySessionId ? `pp-${row.academySessionId}-${row.roomSlug}` : `sess-${row.roomSlug}-${String(row.joinedAt || '').split(' ')[0]}`;
}
export function overlayMutations(state: SyncState, queue: Mutation[]): SyncState {
  const result = copy(state);
  for (const op of queue) {
    if (!op.collection) continue;
    const c = op.collection;
    if (op.kind === 'clear') result[c] = [];
    if (op.kind === 'delete') result[c] = result[c].filter(r => !op.ids?.includes(r.id));
    if (op.kind === 'delete_session') result.attendance = result.attendance.filter(r => attendanceSessionId(r) !== op.sessionId);
    if (op.kind === 'patch') for (const patch of op.items || []) {
      const i = result[c].findIndex(r => r.id === patch.id);
      if (i < 0) result[c].push(copy(patch)); else result[c][i] = {...result[c][i], ...copy(patch)};
    }
  }
  return result;
}

export async function fetchConfirmedJson(url: string, body: unknown, headers: Record<string, string> = {}, transport: typeof fetch = fetch): Promise<any> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await transport(url, {method: 'POST', cache: 'no-store', credentials: 'omit', headers: {'Content-Type': 'application/json', ...headers}, body: JSON.stringify(body), signal: controller.signal});
    if (res.status === 401 || res.status === 403) throw new SyncAuthorizationError('Sesiunea nu mai este autorizată.');
    let data: any;
    try { data = await res.json(); } catch { throw new Error(`Serverul nu a returnat JSON valid (HTTP ${res.status}). Verifică URL-ul și protecția găzduirii.`); }
    if (!res.ok || data?.success !== true) throw new Error(data?.message || data?.error || `Sincronizarea a fost refuzată (HTTP ${res.status}).`);
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw new Error('Serverul nu a confirmat în 15 secunde. Modificările rămân în așteptare.');
    throw error;
  } finally { clearTimeout(timeout); }
}

export class SyncClient {
  private observed: SyncState;
  private queue: Mutation[];
  private flight: Promise<SyncResult> | null = null;
  private requested = false;
  private disposed = false;
  private readonly key: string;
  private readonly initializedKey: string;
  private readonly authorization: Record<string, string>;
  updateToken(token: string): void {
    if (!/^[A-Za-z0-9_.-]{20,8192}$/.test(token)) throw new Error('Sesiune invalidă');
    this.authorization.Authorization = `Bearer ${token}`;
  }
  constructor(private read: () => SyncState, private apply: (s: SyncState, metadata: any) => void, private storage: Pick<Storage, 'getItem' | 'setItem'>, private endpoint: string, private transport: typeof fetch = fetch, private status: (r: SyncResult) => void = () => {}, tenant?: TenantSyncOptions) {
    if (tenant && (!/^(?:license-[1-9][0-9]{0,18}|platform-1)$/.test(tenant.organizationId) || !/^[A-Za-z0-9_.-]{20,8192}$/.test(tenant.token))) throw new Error('Identitate sau sesiune invalidă');
    this.key = tenant ? `vorbello_sync_outbox_v2_${tenant.organizationId}` : 'vorbello_sync_outbox_v2';
    this.initializedKey = tenant ? `vorbello_sync_initialized_v2_${tenant.organizationId}` : 'vorbello_sync_initialized_v2';
    this.authorization = tenant ? {Authorization: `Bearer ${tenant.token}`} : {};
    this.observed = copy(read());
    try { this.queue = JSON.parse(storage.getItem(this.key) || '[]'); } catch { throw new Error('Coada locală de sincronizare nu poate fi citită. Păstrează datele aplicației și contactează administratorul.'); }
    if (!Array.isArray(this.queue)) throw new Error('Coada locală de sincronizare este invalidă.');
    // Old shared data must never be seeded into a newly verified organization.
    if (!tenant && !storage.getItem(this.initializedKey) && !this.queue.some(op => op.kind === 'seed')) this.enqueue({kind: 'seed', seed: this.observed});
  }
  private persist() { this.storage.setItem(this.key, JSON.stringify(this.queue)); }
  /** Invalidates in-flight responses when the authenticated account changes. */
  dispose() { this.disposed = true; this.requested = false; }
  enqueue(op: Omit<Mutation, 'opId'>) {
    if (this.disposed) throw new Error('Sesiunea de sincronizare a fost închisă');
    this.queue.push({...copy(op), opId: globalThis.crypto?.randomUUID?.() || `op-${Date.now()}-${Math.random().toString(36).slice(2)}`});
    this.persist();
  }
  capture(collection?: Collection) {
    const current = copy(this.read());
    for (const c of collection ? [collection] : COLLECTIONS) {
      const before = new Map(this.observed[c].map(r => [r.id, r]));
      const patches: Rows = [];
      for (const row of current[c]) {
        if (!row.id) continue;
        const old = before.get(row.id);
        const patch: Record<string, any> = {id: row.id};
        for (const [key, value] of Object.entries(row)) if (!old || JSON.stringify(old[key]) !== JSON.stringify(value)) patch[key] = value;
        if (Object.keys(patch).length > 1) patches.push(patch);
      }
      if (patches.length) this.enqueue({kind: 'patch', collection: c, items: patches});
      const present = new Set(current[c].map(r => r.id));
      const removed = [...before.keys()].filter(id => !present.has(id));
      if (removed.length) this.remove(c, removed);
      this.observed[c] = current[c];
    }
  }
  remove(collection: Collection, ids: string[]) {
    this.enqueue({kind: 'delete', collection, ids});
    this.observed[collection] = this.observed[collection].filter(r => !ids.includes(r.id));
  }
  clearAttendance() {
    this.enqueue({kind: 'clear', collection: 'attendance'});
    this.observed.attendance = [];
  }
  removeSession(sessionId: string) {
    this.enqueue({kind: 'delete_session', collection: 'attendance', sessionId});
    this.observed.attendance = this.observed.attendance.filter(r => attendanceSessionId(r) !== sessionId);
  }
  sync(): Promise<SyncResult> {
    if (this.disposed) return Promise.resolve({success:false, message:'Sesiunea de sincronizare a fost închisă', pending:this.queue.length});
    this.requested = true;
    if (this.flight) return this.flight;
    this.flight = this.run().finally(() => { this.flight = null; });
    return this.flight;
  }
  private async run(): Promise<SyncResult> {
    let requestAuthorization = this.authorization.Authorization;
    try {
      do {
        this.requested = false;
        const batch = copy(this.queue.slice(0, 100));
        requestAuthorization = this.authorization.Authorization;
        const data = await fetchConfirmedJson(this.endpoint, {protocol: 2, mutations: batch}, this.authorization, this.transport);
        if (this.disposed) throw new Error('Sesiunea de sincronizare a fost închisă');
        if (data.protocol !== 2 || !Array.isArray(data.acknowledged) || !COLLECTIONS.every(c => Array.isArray(data[c]))) throw new Error('Actualizează pachetul web pe app.vorbello.eu: serverul nu confirmă protocolul de sincronizare 2.');
        if (!batch.every(op => data.acknowledged.includes(op.opId))) throw new Error('Serverul nu a confirmat toate modificările. Ele rămân în așteptare.');
        const acknowledged = new Set(batch.map(op => op.opId));
        this.queue = this.queue.filter(op => !acknowledged.has(op.opId));
        this.persist();
        this.storage.setItem(this.initializedKey, 'true');
        const next = overlayMutations(Object.fromEntries(COLLECTIONS.map(c => [c, data[c]])) as SyncState, this.queue);
        this.observed = copy(next);
        this.apply(next, data);
      } while (this.requested || this.queue.length);
      const result = {success: true, message: 'Sincronizat cu serverul', pending: 0};
      this.status(result); return result;
    } catch (error) {
      // A response for an expired token must not revoke a newer verified session.
      if (error instanceof SyncAuthorizationError && !this.disposed && requestAuthorization !== this.authorization.Authorization) return this.run();
      const result: SyncResult = {success: false, message: error instanceof Error ? error.message : 'Server indisponibil.', pending: this.queue.length,
        authorizationLost: error instanceof SyncAuthorizationError};
      if (!this.disposed) this.status(result);
      return result;
    }
  }
}
